import { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import {
  Point,
  TimedPath,
} from '~pages/Admin/Geo/Distances/DistanceMap/catmullRomSpline';

// A fix reported as worse than 100 m is a cell-tower or wifi estimate rather
// than a GPS lock.
const MAX_ACCURACY_IN_METERS = 100;
// 40 m/s is ~90 mph: above any legal highway speed a loaded truck can hold, so
// the ping is a stray fix rather than movement.
const MAX_SPEED_IN_METERS_PER_SECOND = 40;
const EARTH_RADIUS_IN_METERS = 6_371_000;

function toRadians(degrees: number) {
  return (degrees * Math.PI) / 180;
}

function distanceInMeters(from: Point, to: Point) {
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);
  const haversine =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) *
      Math.cos(toRadians(to.lat)) *
      Math.sin(deltaLng / 2) ** 2;

  return (
    2 *
    EARTH_RADIUS_IN_METERS *
    Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
  );
}

interface Fix {
  ms: number;
  point: Point;
}

function impliedSpeedIsImpossible(from: Fix, to: Fix) {
  // Pings can share a timestamp; the 1-second floor keeps the ceiling
  // meaningful instead of dividing by zero.
  const elapsedSeconds = Math.max((to.ms - from.ms) / 1_000, 1);

  return (
    distanceInMeters(from.point, to.point) / elapsedSeconds >
    MAX_SPEED_IN_METERS_PER_SECOND
  );
}

/**
 * The accepted pings in time order — they draw the route dots and drive marker
 * interpolation. Rejecting outliers still matters even though a stray dot is not
 * visually distracting: one ping hundreds of kilometres off-route stretches the
 * map's fit-bounds, opening the replay at the wrong scale. Filters harder than
 * the shared `buildTimedPath`, whose other consumers exist to inspect bad GPS
 * rather than hide it.
 */
export function buildReplayRoutePath(pings: LocationHistoryMinimal[]) {
  const preciseFixes = [...pings]
    .sort((a, b) => a.occurredAt.valueOf() - b.occurredAt.valueOf())
    .map((ping) => ({
      accuracy: ping.accuracy === null ? null : parseFloat(ping.accuracy),
      ms: ping.occurredAt.valueOf(),
      point: { lat: parseFloat(ping.lat), lng: parseFloat(ping.lon) },
    }))
    .filter(({ accuracy, point }) => {
      const isNullIsland = Math.abs(point.lat) <= 0.01 && Math.abs(point.lng) <= 0.01;
      const isImprecise = accuracy !== null && accuracy > MAX_ACCURACY_IN_METERS;

      return !isNullIsland && !isImprecise;
    });

  // The first fix has no predecessor to measure its speed against, so it is only
  // rejected when the next fix contradicts it and agrees with the one after it.
  // Two mutually contradicting fixes name no culprit, so the earlier one stands.
  const firstFixIsStray =
    preciseFixes.length > 2 &&
    impliedSpeedIsImpossible(preciseFixes[0], preciseFixes[1]) &&
    !impliedSpeedIsImpossible(preciseFixes[1], preciseFixes[2]);

  const timedPath: TimedPath = { pingTimes: [], rawPath: [] };
  let previous: Fix | null = null;

  for (const fix of preciseFixes.slice(firstFixIsStray ? 1 : 0)) {
    if (previous && impliedSpeedIsImpossible(previous, fix)) continue;

    timedPath.rawPath.push(fix.point);
    timedPath.pingTimes.push(fix.ms);
    previous = fix;
  }

  return timedPath;
}
